/**
 * useHomeFirstRunState
 *
 * Derives the first-time-user state for the Home screen from existing contexts.
 * Used to conditionally render the onboarding experience vs normal home.
 */

import { useMemo } from 'react';
import { useDashboard } from '../contexts/DashboardContext';
import { useDataModels } from '../contexts/DataModelsContext';
import { useConnections } from '../contexts/ConnectionsContext';

/**
 * Workspace lifecycle states — drives the Home layout.
 */
export type WorkspaceState =
  | 'empty'         // No sources at all
  | 'missing_model' // Source exists, no model, no dashboards
  | 'building'      // Active build/sync job in progress
  | 'model_only'    // Model exists, no dashboards yet
  | 'ready';        // Dashboards exist — normal home

const WELCOME_DISMISSED_KEY = 'home-welcome-card-dismissed';

export interface HomeFirstRunState {
  /** Derived workspace lifecycle state */
  workspaceState: WorkspaceState;
  /** User has at least one data source connection */
  hasDataSources: boolean;
  /** User has at least one data model */
  hasModels: boolean;
  /** User has at least one dashboard */
  hasDashboards: boolean;
  /** There is an active build/sync job in progress */
  hasActiveBuildJob: boolean;
  /** True when user has nothing: no sources, no models, no dashboards */
  isFirstRunEmpty: boolean;
  /** Has source but workspace is still being built (no completed dashboards yet) */
  isFirstBuildInProgress: boolean;
  /** First build is complete and the welcome card has not been dismissed */
  isFirstBuildComplete: boolean;
  /** Connector ID of the first/active connection (for source-aware messaging) */
  activeConnectorId: string | null;
  /** Name of the first/active connection source */
  activeSourceName: string | null;
  /** Whether data is still loading from contexts */
  isLoading: boolean;
}

export function useHomeFirstRunState(): HomeFirstRunState {
  const { dashboards, isLoading: dashboardsLoading } = useDashboard();
  const { dataModels, isLoading: modelsLoading } = useDataModels();
  const { connections, loading: connectionsLoading } = useConnections();

  return useMemo(() => {
    const isLoading = dashboardsLoading || modelsLoading || connectionsLoading;

    const hasDataSources = connections.length > 0;
    const hasModels = dataModels.length > 0;
    const hasDashboards = dashboards.length > 0;

    // Detect active build/sync jobs
    const inProgressStatuses = ['syncing', 'draft', 'authenticating', 'configuring'];
    const hasActiveSource = connections.some(c => inProgressStatuses.includes(c.status));
    const hasActiveModel = dataModels.some(m => m.status === 'building');
    const hasActiveBuildJob = hasActiveSource || hasActiveModel;

    // First run empty: no data at all
    const isFirstRunEmpty = !hasDataSources && !hasModels && !hasDashboards;

    // First build in progress: only while an active build/sync job is running
    // AND no dashboards exist yet (otherwise show normal home with progress banner)
    const isFirstBuildInProgress = !isFirstRunEmpty && !hasDashboards && hasActiveBuildJob;

    // First build complete: has a model and welcome card not dismissed
    const welcomeDismissed = localStorage.getItem(WELCOME_DISMISSED_KEY) === 'true';
    const hasCompletedModel = dataModels.some(m => m.status === 'active');
    const isFirstBuildComplete = hasCompletedModel && !hasDashboards && !welcomeDismissed;

    // Derive active connector info from first connection (or active syncing one)
    const activeConnection =
      connections.find(c => inProgressStatuses.includes(c.status)) ?? connections[0] ?? null;
    const activeConnectorId = activeConnection?.connectorId ?? null;
    const activeSourceName = activeConnection?.name ?? null;

    // Derive workspace state
    let workspaceState: WorkspaceState;
    if (!hasDataSources && !hasModels && !hasDashboards) {
      workspaceState = 'empty';
    } else if (hasActiveBuildJob) {
      workspaceState = 'building';
    } else if (hasDataSources && !hasModels && !hasDashboards) {
      workspaceState = 'missing_model';
    } else if (hasModels && !hasDashboards) {
      workspaceState = 'model_only';
    } else {
      workspaceState = 'ready';
    }

    return {
      workspaceState,
      hasDataSources,
      hasModels,
      hasDashboards,
      hasActiveBuildJob,
      isFirstRunEmpty,
      isFirstBuildInProgress,
      isFirstBuildComplete,
      activeConnectorId,
      activeSourceName,
      isLoading,
    };
  }, [dashboards, dataModels, connections, dashboardsLoading, modelsLoading, connectionsLoading]);
}

/** Dismiss the welcome completion card */
export function dismissWelcomeCard(): void {
  localStorage.setItem(WELCOME_DISMISSED_KEY, 'true');
}